import { useEffect, useState, useCallback } from "react";
import { Loader2, Wallet } from "lucide-react";
import { getPayments } from "../lib/api";
import { PaymentCard } from "./PaymentCard";
import { RevenueView } from "./RevenueView";
import { CarrierStats } from "./CarrierStats";
import { paymentTotals, money } from "../lib/payments";

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-[#13161C] border border-[#262C38] rounded-md px-4 py-3">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-heading text-xl font-bold text-white mt-1">{value}</p>
    {sub && <p className="font-mono text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

export const Payments = () => {
  const [payments, setPayments] = useState(null);
  const load = useCallback(() => getPayments().then(({ data }) => setPayments(data)), []);
  useEffect(() => { load(); }, [load]);
  const replace = (p) => setPayments((list) => list.map((x) => (x.id === p.id ? p : x)));

  if (!payments) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
  const t = paymentTotals(payments);

  return (
    <div data-testid="payments-page" className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="payments-stat-carrier-received" label="Received from carriers" value={money(t.carrierReceived)} sub={`of ${money(t.carrierExpected)} expected`} />
        <Stat testid="payments-stat-carrier-outstanding" label="Carriers still owe" value={money(t.carrierExpected - t.carrierReceived)} />
        <Stat testid="payments-stat-setter-sent" label="Sent to setters" value={money(t.setterSent)} sub={`of ${money(t.setterOwed)} owed`} />
        <Stat testid="payments-stat-due-now" label="Due today / overdue" value={t.dueNow} sub="payments to chase or send" />
      </div>
      <p className="font-mono text-[11px] text-slate-500">
        Drivers appear here when marked Hired. Each payment is split 50% on the start day and 50% two weeks later. Carrier invoices go out automatically at 9am ET on the due date (with your ACH details) once a billing email is saved. Late carriers get a reminder on the 1st working day after the due date, then a daily overdue email with a 3% per working day late fee (Mon–Fri, US federal holidays skipped) until you tick it received. Tick a box when money arrives or is sent in Payoneer.
      </p>
      <RevenueView payments={payments} />
      <CarrierStats payments={payments} />
      {payments.length === 0 ? (
        <div data-testid="payments-empty" className="py-16 text-center border border-dashed border-[#262C38] rounded-md">
          <Wallet className="h-6 w-6 mx-auto text-slate-600 mb-2" />
          <p className="text-slate-400 text-sm">No hired drivers yet.</p>
          <p className="text-slate-600 text-xs font-mono mt-1">Tick "Hired" on a lead card to start tracking payments.</p>
        </div>
      ) : (
        payments.map((p) => <PaymentCard key={p.id} payment={p} onChange={replace} />)
      )}
    </div>
  );
};
